import React, { useMemo } from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Image,
  Text,
  Box,
} from '@chakra-ui/react';
import { Match } from '../../types';
import { MundialTeam } from '../../types/mundial';

interface MundialMatchCalendarProps {
  matches: Match[];
  teamsByCode: Map<string, MundialTeam>;
}

export const MundialMatchCalendar: React.FC<MundialMatchCalendarProps> = ({
  matches,
  teamsByCode,
}) => {
  const sortedMatches = useMemo(
    () =>
      [...matches].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      ),
    [matches]
  );

  const renderTeam = (name: string, code?: string | null) => {
    const team = code ? teamsByCode.get(code.toUpperCase()) : undefined;
    const flag = team?.flag_url;
    return (
      <Box display="flex" alignItems="center" gap={2}>
        {flag && (
          <Image src={flag} alt={name} boxSize="20px" borderRadius="sm" />
        )}
        <Text>{name}</Text>
      </Box>
    );
  };

  return (
    <Box overflowX="auto">
      <Table size="sm" variant="striped">
        <Thead>
          <Tr>
            <Th>Fecha</Th>
            <Th>Hora</Th>
            <Th>Grupo / Fase</Th>
            <Th>Estadio</Th>
            <Th>Local</Th>
            <Th>Visitante</Th>
            <Th>Resultado</Th>
          </Tr>
        </Thead>
        <Tbody>
          {sortedMatches.map((m) => {
            const date = new Date(m.date);
            const hasResult = m.score_a !== null && m.score_b !== null;
            return (
              <Tr key={m.id}>
                <Td>
                  {date.toLocaleDateString('es-MX', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                  })}
                </Td>
                <Td>
                  {date.toLocaleTimeString('es-MX', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Td>
                <Td>{m.group ? `Grupo ${m.group}` : m.phase}</Td>
                <Td>{m.stadium || '—'}</Td>
                <Td>{renderTeam(m.team_a, m.team_a_code)}</Td>
                <Td>{renderTeam(m.team_b || '', m.team_b_code)}</Td>
                <Td>
                  {hasResult ? (
                    <Badge colorScheme="pitch">
                      {m.score_a} - {m.score_b}
                    </Badge>
                  ) : (
                    <Badge colorScheme="orange">Pendiente</Badge>
                  )}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
};
